import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import { useWindowWidth } from '../hooks';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import StockRecord from '../components/StockRecord';
import StockItemForm from '../components/StockItemForm';
import StockMadeFrom from '../components/StockMadeFrom';
import StockUndoBar from '../components/StockUndoBar';
import { TEAL, AMBER, RED, BORD, CARD, DARK, MUTED, DIM, TEXT, MOVES, kindOf, num, qty, packs, day, when, perOne, Chip, missingLink } from '../stockShared';

// One stock item (the stock module, 22 Oct 2026): what is on hand, its
// minimum and last count, which products use it, the MouldOS record it is,
// and every movement ever recorded -- who, when, how many, and what was left
// after it. Nothing here is ever changed afterwards; a mistake gets a
// correcting line.

// A packaging item opens its own editor in Admin → Packaging (29 Oct 2026).
const LINK_TO = { part: (l) => `/admin/parts/${l.id}`, packaging: (l) => `/admin/packaging?container=${l.id}`, material: () => '/admin/materials' };
const LINK_WORD = { part: 'Part', product: 'Product', packaging: 'Packaging', material: 'Material' };

export default function StockItem() {
  const { id } = useParams();
  const { toast } = useToast();
  const manage = useAuth().can('manage');   // changing the item and its list (28 Oct 2026)
  const phone = useWindowWidth() < 700;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [recording, setRecording] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addingInto, setAddingInto] = useState(false);   // + Item on a moulded part: what is moulded into it
  const [undo, setUndo] = useState(null);           // { ids, text }: what was just recorded, undoable for 10 s
  // Undo from the history (27 Oct 2026: a mistake seen after the 10 s were
  // up): the person's own lines for a day, two taps so a slip takes nothing back.
  const [askUndo, setAskUndo] = useState(null);     // the line asked about
  const [undoing, setUndoing] = useState(false);
  const [all, setAll] = useState([]);           // every item, to pick what it is made from

  async function undoLine(m) {
    setUndoing(true);
    try {
      await api.post('/api/v1/stock/movements/undo', { ids: [m.id] });
      toast.success('Undone: back to how it was. The history keeps both lines.');
      setAskUndo(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Not undone. Check the connection and try again.');
    } finally {
      setUndoing(false);
    }
  }

  const load = useCallback(() => {
    api.get(`/api/v1/stock/items/${id}`)
      .then(r => { setData(r.data); setError(null); })
      .catch(err => setError(err.response?.data?.error || 'Could not read this item. Check the connection and reload.'));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/api/v1/stock').then(r => setAll(r.data.items || [])).catch(() => {}); }, [id]);
  // Whether runs of a part sold as it is reach it yet (Automatic stock, 3 Nov 2026).
  const [soldOn, setSoldOn] = useState(null);
  useEffect(() => { api.get('/api/v1/stock/auto').then(r => setSoldOn(!!(r.data.switches || []).find(s => s.key === 'sold')?.on)).catch(() => setSoldOn(null)); }, []);

  const it = data?.item;
  const lv = it?.level;
  const k = it ? kindOf(it.kind) : null;
  const btn = (primary) => ({ background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL, border: `1px solid ${primary ? TEAL : `${TEAL}88`}`,
    borderRadius: 9, padding: '9px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui', minHeight: 42 });

  return (
    <div style={{ padding: phone ? '14px 12px 90px' : '20px 24px 60px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box', maxWidth: 980, margin: '0 auto' }}>
      <Link to="/stock" style={{ color: MUTED, fontSize: 13, textDecoration: 'none' }}><i className="fa-solid fa-chevron-left" style={{ fontSize: 10, marginRight: 6 }} />Stock</Link>
      {error && <div style={{ color: RED, fontSize: 13.5, marginTop: 14 }}>{error}</div>}
      {!data && !error && <div style={{ color: MUTED, fontSize: 13.5, marginTop: 14 }}>Reading…</div>}

      {it && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 10 }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>{it.name}</h1>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, fontSize: 12.5, color: MUTED }}>
                <Chip color={k.color} regular>{k.label}</Chip>
                {!it.active && <Chip color={AMBER}>retired</Chip>}
                {it.chamfer && <Chip color={TEAL}>kept as pofrezkane and not</Chip>}
                {it.part_id != null && (it.from_production !== false
                  ? <Chip color={TEAL} regular title="Good parts recorded when a run ends on Today go into its stock">from production{Number(it.per_part) !== 1 ? ` · ${perOne(it.per_part)} per good part` : ''}</Chip>
                  : it.kind === 'moulded' && !(it.products || []).length
                    ? (soldOn
                      ? <Chip color={TEAL} regular title="A part sold as it is: runs go onto its shelf because Automatic stock is on">from production · Automatic stock</Chip>
                      : <Chip color={MUTED} regular title="A part sold as it is: runs go onto its shelf once Automatic stock is switched on (Stock → Automatic stock usage)">runs go onto the shelf once Automatic stock is on</Chip>)
                    : <Chip color={MUTED} regular title="Production does not add to it; switch it on with Change">production does not add to it</Chip>)}
                {it.link && (LINK_TO[it.link.type] && (it.link.type !== 'packaging' || manage)
                  ? <Link to={LINK_TO[it.link.type](it.link)} title={it.link.type === 'packaging' ? 'Open it in Admin → Packaging' : undefined} style={{ color: TEAL, textDecoration: 'none' }}>{LINK_WORD[it.link.type]}: {it.link.name}{it.link.type === 'packaging' && <i className="fa-solid fa-pen" style={{ marginLeft: 6, fontSize: 10 }} />}</Link>
                  : <span>{LINK_WORD[it.link.type]}: {it.link.name}</span>)}
                {!it.link && !missingLink(it) && <span>Not in MouldOS</span>}
              </div>
              {missingLink(it) && (
                <div style={{ marginTop: 8, fontSize: 13, color: AMBER, lineHeight: 1.5 }}>
                  Not linked to {missingLink(it).to}, so {missingLink(it).why}.{' '}
                  {manage ? <>Press Change and {missingLink(it).pick}.</> : 'A director links it with Change.'}
                </div>
              )}
              {/* What is left to finish it (29 Oct 2026), the link apart (said above). */}
              {(it.todo || []).filter(t => !t.startsWith('link it')).length > 0 && (
                <div style={{ marginTop: 6, fontSize: 13, color: AMBER, lineHeight: 1.5 }}>
                  To finish: {it.todo.filter(t => !t.startsWith('link it')).map(t => (t === 'count it' ? 'count it (Record → Counted)'
                    : t === 'say where it goes' ? (manage ? 'say where it goes (Change → Where does it go?)' : 'say where it goes (a director does, with Change)')
                      : t.startsWith('list what it is made of') ? 'list what it is made of (BOM → Build steps)' : t)).join(' · ')}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setRecording(true)} style={btn(true)} disabled={!it.active}><i className="fa-solid fa-plus-minus" style={{ marginRight: 6 }} />Record</button>
              {manage && <button type="button" onClick={() => setEditing(true)} style={btn(false)}><i className="fa-solid fa-pen" style={{ marginRight: 6 }} />Change</button>}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr 1fr' : 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 16 }}>
            <Fig label="On hand" big tone={!lv.counted ? MUTED : it.below_min ? RED : lv.total < 0 ? AMBER : TEXT}
              value={lv.counted || lv.total ? qty(lv.total, it) : '—'} sub={packs(lv.total, it) || (!lv.counted ? 'not counted yet' : null)} />
            {it.chamfer && <Fig label="Pofrezkane" value={qty(lv.done, it)} />}
            {it.chamfer && <Fig label="Not pofrezkane" value={qty(lv.raw, it)} />}
            <Fig label="Minimum" value={it.min_qty != null ? qty(it.min_qty, it) : '—'} sub={it.below_min ? 'below it' : it.min_qty == null ? 'not set' : null} tone={it.below_min ? RED : TEXT} />
            {it.can_make && <Fig label={it.kind === 'moulded' ? 'Inserts for' : 'Can make'} tone={it.can_make.n > 0 ? TEAL : AMBER}
              value={it.can_make.loop ? '—' : `${num(it.can_make.n)} more`}
              sub={it.can_make.loop ? 'its inputs go round in a loop' : it.can_make.short[0] ? `then ${it.can_make.short[0].name} runs out` : 'from what is in stock'} />}
            <Fig label="Last counted" value={lv.last_count ? day(lv.last_count) : '—'} sub={!lv.counted ? 'count it to start' : null} tone={lv.counted ? TEXT : AMBER} />
          </div>

          <div style={{ marginTop: 14, fontSize: 13, color: DIM }}>
            {it.products.length
              ? <>Used in: {it.products.map((p, n) => <span key={p.id}>{n ? ', ' : ''}<b style={{ color: TEXT }}>{p.name}</b></span>)}</>
              : 'On no product\'s list.'}
            {/* How the product is put together, step by step (Build steps, 27 Oct 2026). */}
            {/* A moulded part's own BOM (29 Oct 2026). */}
            {it.kind === 'moulded' && it.part_id && (
              <Link to={`/stock/build/part/${it.id}`} style={{ marginLeft: 12, color: TEAL, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                <i className="fa-solid fa-diagram-project" style={{ marginRight: 6, fontSize: 11 }} />BOM
              </Link>
            )}
            {manage && it.kind === 'moulded' && it.part_id && (
              <Link to={`/admin/packaging?tab=items&part=${it.part_id}`} title="How it is packed, in Admin → Packaging" style={{ marginLeft: 12, color: TEAL, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                <i className="fa-solid fa-box-open" style={{ marginRight: 6, fontSize: 11 }} />Packing
              </Link>
            )}
            {manage && it.kind === 'moulded' && it.part_id && (
              <button type="button" onClick={() => setAddingInto(true)} title={`Something moulded into ${it.name}, like a label placed in the mould`}
                style={{ marginLeft: 12, background: 'none', border: 'none', padding: 0, color: TEAL, fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'system-ui', whiteSpace: 'nowrap' }}>
                <i className="fa-solid fa-plus" style={{ marginRight: 5, fontSize: 10 }} />Item
              </button>
            )}
            {it.kind === 'finished' && (it.product_id || it.products.length === 1) && (
              <Link to={`/stock/build/${it.product_id || it.products[0].id}`} style={{ marginLeft: 12, color: TEAL, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                <i className="fa-solid fa-diagram-project" style={{ marginRight: 6, fontSize: 11 }} />BOM
              </Link>
            )}
            {it.note && <div style={{ marginTop: 6, color: MUTED }}>{it.note}</div>}
          </div>

          <StockMadeFrom canEdit={manage} item={it} items={all} hints={data.mouldos_parts || []} onSaved={() => { toast.success(`What ${it.name} is made from is saved.`); load(); }} />

          <h2 style={{ fontSize: 15, fontWeight: 800, margin: '22px 0 8px' }}>History <span style={{ color: MUTED, fontWeight: 600, fontSize: 12.5 }}>{data.history.length} line{data.history.length === 1 ? '' : 's'} · newest first</span></h2>
          {!data.history.length && <div style={{ color: MUTED, fontSize: 13.5 }}>Nothing recorded yet. Start with a count: Record → Counted.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, background: CARD, border: data.history.length ? `1px solid ${BORD}` : 'none', borderRadius: 12, overflow: 'hidden' }}>
            {data.history.map((m, n, all) => {
              const undone = all.some(x => x.undoes === m.id);
              const mv = MOVES[m.kind] || { label: m.kind, color: DIM, icon: 'fa-circle' };
              const amount = m.kind === 'counted' ? `= ${qty(m.qty, it)}` : m.kind === 'chamfered' ? qty(m.qty, it) : `${m.effect > 0 ? '+' : '−'}${qty(Math.abs(m.effect), it)}`;
              const side = it.chamfer && m.kind !== 'chamfered' ? (m.chamfered ? 'pofrezkane' : 'not pofrezkane') : null;
              return (
                <div key={m.id} style={{ display: 'flex', gap: 12, padding: '10px 14px', borderTop: n ? `1px solid ${BORD}` : 'none', alignItems: 'flex-start', flexWrap: phone ? 'wrap' : 'nowrap', opacity: undone || m.undoes ? 0.6 : 1 }}>
                  <div style={{ width: phone ? '100%' : 150, flexShrink: 0, fontSize: 12.5, color: DIM }}>{when(m.happened_at)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ color: mv.color, fontWeight: 800, fontSize: 13.5 }}><i className={`fa-solid ${mv.icon}`} style={{ fontSize: 11, marginRight: 6 }} />{mv.label}</span>
                      <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', textDecoration: undone ? 'line-through' : 'none' }}>{amount}</span>
                      {side && <span style={{ fontSize: 12, color: DIM }}>{side}</span>}
                      {m.source === 'import' && <Chip color={MUTED}>from the Excel</Chip>}
                      {m.source === 'production' && <Chip color={TEAL} regular>from production{m.job_id ? ` · job ${m.job_id}` : ''}</Chip>}
                      {m.source === 'assembly' && <Chip color={MUTED} regular>used by a Made line</Chip>}
                      {undone && <Chip color={MUTED}>undone</Chip>}
                      {m.undoes && <Chip color={MUTED} regular>Undo of line {m.undoes}</Chip>}
                    </div>
                    <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>
                      {m.recorded_by || 'Someone'}
                      {m.lot ? ` · batch ${m.lot}` : ''}
                      {m.note ? ` · ${m.note}` : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 12.5, color: DIM, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    then {qty(m.after.total, it)}
                    {it.chamfer && <div style={{ fontSize: 11.5 }}>{num(m.after.done)} pofrezkane · {num(m.after.raw)} not</div>}
                    {m.can_undo && (askUndo === m.id ? (
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                        <button type="button" onClick={() => setAskUndo(null)} disabled={undoing}
                          style={{ background: 'transparent', border: `1px solid ${BORD}`, color: DIM, borderRadius: 8, padding: '5px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui', minHeight: 34 }}>Keep it</button>
                        <button type="button" onClick={() => undoLine(m)} disabled={undoing}
                          style={{ background: TEAL, border: `1px solid ${TEAL}`, color: '#0f172a', borderRadius: 8, padding: '5px 10px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'system-ui', minHeight: 34 }}>{undoing ? 'Undoing…' : 'Undo it'}</button>
                      </div>
                    ) : (
                      <div style={{ marginTop: 6 }}>
                        <button type="button" onClick={() => setAskUndo(m.id)} title="Takes this line back: the opposite is recorded, and the history keeps both."
                          style={{ background: 'transparent', border: `1px solid ${TEAL}88`, color: TEAL, borderRadius: 8, padding: '5px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui', minHeight: 34 }}>
                          <i className="fa-solid fa-rotate-left" style={{ marginRight: 6, fontSize: 11 }} />Undo
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {recording && it && (
        <StockRecord item={it} onClose={() => setRecording(false)}
          onSaved={(levels, m, ids) => {
            setRecording(false);
            setUndo({ ids, text: `${it.name}: ${m.kind === 'chamfered' ? 'made pofrezkane' : (MOVES[m.kind]?.label || m.kind).toLowerCase()} ${qty(m.qty, it)}.${levels[it.id] ? ` Now ${qty(levels[it.id].total, it)}.` : ''}` });
            load();
          }} />
      )}
      {undo && undo.ids.length > 0 && (
        <StockUndoBar key={undo.ids.join(',')} text={undo.text} ids={undo.ids} onClose={() => setUndo(null)} onUndone={() => load()} />
      )}
      {addingInto && (
        <StockItemForm start={{ part_item_id: it.id, part_name: it.name }} onClose={() => setAddingInto(false)}
          onSaved={(saved) => { setAddingInto(false); toast.success(`${saved.name} is moulded into ${it.name}.`); load(); }} />
      )}
      {editing && it && (
        <StockItemForm item={it} onClose={() => setEditing(false)}
          onSaved={(saved) => { setEditing(false); toast.success(saved.active ? `${saved.name} saved.` : `${saved.name} retired; its history is kept.`); load(); }} />
      )}
    </div>
  );
}

function Fig({ label, value, sub, big, tone = TEXT }) {
  return (
    <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: 12, padding: '10px 12px', minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: MUTED }}>{label}</div>
      <div style={{ fontSize: big ? 24 : 17, fontWeight: 800, color: tone, marginTop: 3, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: DIM, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}
